import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { useToast } from "vue-toast-notification";

import store from "@/store";

import TokenService from "../token.service";

interface ApiResponse<T = any> {
  success: boolean;
  status: number;
  message: string;
  result: T;
}

const SERVER_ERROR_MSG = "Oops!, Something went wrong, Please try again later or contact an admin";

const authFreeRoutes = [
  "/register",
  "/login",
  "/forgot-password",
  "/reset-password",
  "/mfa-authentication",
  "/terms",
];

const isAuthFreeRoute = authFreeRoutes.some(path => location.pathname.startsWith(path));

let isRefreshing: boolean = false;
let refreshPromise: Promise<any> | null = null;
let failedQueue: Array<{ resolve: (v: any) => void; reject: (v: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(promise => {
    if (error) promise.reject(error);
    else promise.resolve(token);
  });
  
  failedQueue = [];
};

const $toast = useToast();

const api: AxiosInstance = axios.create();

api.interceptors.request.use(
  config => {
    const token = TokenService.getAccessToken();
    const username = localStorage.getItem("mcAdminUsername");
    const SessionID = sessionStorage.getItem("sessionToken");
    if (token) {
      config.headers.Authorization = config.url?.includes("userlogin/verify")
        ? token
        : `Bearer ${token}`;
    };

    if (username) {
      config.headers["username"] = username;
    };

    if (SessionID) {
      config.headers["SessionID"] = SessionID;
    };

    const isFormData = config.data instanceof FormData;
    if (isFormData) {
      config.headers["Content-Type"] = "multipart/form-data";
    } else {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  error =>  Promise.reject(error instanceof Error ? error : new Error(error))
);

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;
    const isRefreshCall = typeof originalRequest?.url === "string" && (originalRequest.url.includes("/refreshtoken") || originalRequest.url.includes("/userlogout"));

    if (status === 401 && !originalRequest._retry && !isAuthFreeRoute && !isRefreshCall) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise ((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return api(originalRequest);
        })
      }

      isRefreshing = true;

      try {
        refreshPromise = store.dispatch("auth/REFRESH_TOKEN");

        const newToken = await refreshPromise;

        processQueue(null, newToken);
        isRefreshing = false;

        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        isRefreshing = false;

        await store.dispatch("auth/LOGOUT", { redirect: false, force: true });
        return Promise.reject(err);
      }
    };

    if (status === 500) {
      $toast.error(SERVER_ERROR_MSG, { position: "top-right", queue: true });
    };

    if (!error.response) {
      $toast.error(
        `${error.message ?? error ?? "Network Error"}. Please check your internet or try again later.`,
        { position: "top-right", queue: true }
      );
    };

    return Promise.reject(error instanceof Error ? error : new Error(error));
  }
);

function checkStatus<T>(res: AxiosResponse<any>): ApiResponse<T> {
  const { result, message } = res.data;

  return {
    success: res.status >= 200 && res.status < 300,
    status: res.status,
    message,
    result
  }
};

function handleError<T>(err: any): ApiResponse<T> {
  const status = err?.response?.status ?? 500;
  const message =
    err?.response?.data?.message ??
    err?.message ??
    'Something went wrong';
  const result = err?.response?.data?.result;

  return {
    success: false,
    status,
    message,
    result
  };
};

export async function get<T = any>(
  url: string,
  config: AxiosRequestConfig = {}
): Promise<ApiResponse<T>> {
  try {
    const res = await api.get(url, { ...config });
    return checkStatus<T>(res);
  } catch (err) {
    return handleError<T>(err);
  }
};

export async function post<T = any>(
  url: string,
  payload: any = {},
  config: AxiosRequestConfig = {}
): Promise<ApiResponse<T>> {
  try {
    const res = await api.post(url, { ...payload }, { ...config });
    return checkStatus<T>(res);
  } catch (err) {
    return handleError<T>(err);
  }
};

export async function put<T = any>(
  url: string,
  payload: any = {},
  config: AxiosRequestConfig = {}
): Promise<ApiResponse<T>> {
  try {
    const res = await api.put(url, { ...payload }, { ...config });
    return checkStatus<T>(res);
  } catch (err) {
    return handleError<T>(err);
  }
};

export async function del<T = any>(
  url: string,
  payload: any = {},
  config: AxiosRequestConfig = {}
): Promise<ApiResponse<T>> {
  try {
    const res = await api.delete(url, {
      data: payload,
      ...config
    });
    return checkStatus<T>(res);
  } catch (err) {
    return handleError<T>(err);
  }
};

export { api };
